// Avviksvarsling (bevegelse + kommunikasjon). Regelbasert deteksjon av det som
// «ikke stemmer» i sporbildet — grunnlag for tidlig varsling (ML kan senere mate
// samme kø). Bevegelse: umulig hopp (spoof/id-forveksling), loitering
// (overvåkning), brå kursreversering. Kommunikasjon: samband falt (node som
// sluttet å melde), meldingsflom. Produserer `Alert` (kind 'anomaly') så avvik
// renner inn i samme prioriterte kø som HVT/TST-alarmer (alerts.ts).
import { haversineM } from './geo.js';
/** Fysisk maks-fart (m/s) — over dette er et hopp datafeil/spoof, ikke bevegelse. */
export const PHYS_MAX_MPS = 1000; // ~Mach 3
function bearing(a, b) {
    const φ1 = a.lat * Math.PI / 180, φ2 = b.lat * Math.PI / 180;
    const dλ = (b.lon - a.lon) * Math.PI / 180;
    const y = Math.sin(dλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}
function angleDiff(a, b) {
    const d = Math.abs(a - b) % 360;
    return d > 180 ? 360 - d : d;
}
/**
 * Bevegelsesavvik for ett spor gitt dets posisjonshistorikk (eldst→nyest).
 * `refId`/`label` brukes til å merke varslene.
 */
export function movementAnomalies(refId, label, history, opts = {}) {
    const physMax = opts.physMaxMps ?? PHYS_MAX_MPS;
    const loiterR = opts.loiterRadiusM ?? 50;
    const loiterMs = opts.loiterMinMs ?? 10 * 60_000;
    const loiterN = opts.loiterMinFixes ?? 5;
    const revDeg = opts.reversalDeg ?? 150;
    const out = [];
    if (history.length < 2)
        return out;
    // Umulig hopp (teleport).
    for (let i = 1; i < history.length; i++) {
        const a = history[i - 1], b = history[i];
        const dt = (b.tsMs - a.tsMs) / 1000;
        if (dt <= 0)
            continue;
        const d = haversineM({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
        const v = d / dt;
        if (v > physMax) {
            out.push({ key: `anom:teleport:${refId}:${b.tsMs}`, kind: 'anomaly', severity: 'advarsel', refId, tsMs: b.tsMs,
                title: `Umulig hopp: ${label}`, detail: `${Math.round(v)} m/s implisert over ${Math.round(dt)} s — mulig spoof/id-forveksling` });
        }
    }
    // Brå kursreversering.
    for (let i = 2; i < history.length; i++) {
        const p0 = history[i - 2], p1 = history[i - 1], p2 = history[i];
        const b1 = bearing({ lat: p0.lat, lon: p0.lon }, { lat: p1.lat, lon: p1.lon });
        const b2 = bearing({ lat: p1.lat, lon: p1.lon }, { lat: p2.lat, lon: p2.lon });
        if (angleDiff(b1, b2) >= revDeg) {
            out.push({ key: `anom:reversal:${refId}:${p2.tsMs}`, kind: 'anomaly', severity: 'info', refId, tsMs: p2.tsMs,
                title: `Kursreversering: ${label}`, detail: `Retningsendring ~${Math.round(angleDiff(b1, b2))}°` });
            break; // ett varsel er nok
        }
    }
    // Loitering: alle punkter innen radius over lang tid.
    if (history.length >= loiterN) {
        const c = history[0];
        const withinAll = history.every((f) => haversineM({ lat: c.lat, lon: c.lon }, { lat: f.lat, lon: f.lon }) <= loiterR);
        const span = history[history.length - 1].tsMs - history[0].tsMs;
        if (withinAll && span >= loiterMs) {
            out.push({ key: `anom:loiter:${refId}`, kind: 'anomaly', severity: 'advarsel', refId, tsMs: history[history.length - 1].tsMs,
                title: `Loitering: ${label}`, detail: `Innen ${loiterR} m i ${Math.round(span / 60_000)} min — mulig overvåkning` });
        }
    }
    return out;
}
/** Kommunikasjonsavvik: samband falt (stillhet) + meldingsflom. */
export function commsAnomalies(nodes, nowMs, opts = {}) {
    const factor = opts.silenceFactor ?? 3;
    const defMs = opts.silenceMsDefault ?? 5 * 60_000;
    const flood = opts.floodPerMin ?? 120;
    const out = [];
    for (const n of nodes) {
        const silence = nowMs - n.lastSeenMs;
        const threshold = n.expectedIntervalMs ? n.expectedIntervalMs * factor : defMs;
        if (silence > threshold) {
            const severity = silence > threshold * 3 ? 'kritisk' : 'advarsel';
            out.push({ key: `anom:silence:${n.id}`, kind: 'anomaly', severity, refId: n.id, tsMs: nowMs,
                title: `Samband falt: ${n.label}`, detail: `Ingen melding på ${Math.round(silence / 60_000)} min` });
        }
        if (n.msgPerMin !== undefined && n.msgPerMin >= flood) {
            out.push({ key: `anom:flood:${n.id}`, kind: 'anomaly', severity: 'advarsel', refId: n.id, tsMs: nowMs,
                title: `Meldingsflom: ${n.label}`, detail: `${n.msgPerMin}/min — mulig feil/DoS` });
        }
    }
    return out;
}
//# sourceMappingURL=anomaly.js.map